import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, RotateCcw } from "lucide-react";
import { AdminTitle } from "./AdminLayout";
import { ARRANGEMENTS, EMP_TYPES } from "./JobsAdmin";
import { http, apiError } from "../../lib/api";

const KEYS = ["employment_type", "work_arrangement", "location", "compensation"];
const NOVIS = { employment_type: false, location: false, compensation: false, work_arrangement: false };
const pick = (j) => ({ id: j.id, employment_type: j.employment_type || "", work_arrangement: j.work_arrangement || "", location: j.location || "", compensation: j.compensation || "", visible: { ...NOVIS, ...(j.visible || {}) }, published: !!j.published });
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const Cell = ({ row, k, onChange, children }) => (
  <td className="p-2 align-top min-w-[170px]">
    {children}
    <label className={`flex items-center gap-1.5 mt-1.5 text-[0.7rem] ${row[k] ? "text-slate-300" : "text-slate-500"}`}>
      <input type="checkbox" className="accent-[var(--gold)]" disabled={!row[k]} checked={!!row[k] && row.visible[k]} onChange={(e) => onChange({ visible: { ...row.visible, [k]: e.target.checked } })} data-testid={`details-show-${k}-${row.id}`} />
      {row[k] ? (row.visible[k] ? "Shown publicly" : "Hidden") : "Hidden until filled in"}
    </label>
  </td>
);

export default function JobDetailsAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-jobs"], queryFn: async () => (await http.get("/admin/careers/jobs")).data });
  const jobs = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data]);
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRows(Object.fromEntries(jobs.map((j) => [j.id, pick(j)]))); }, [jobs]);
  const dirty = jobs.filter((j) => rows[j.id] && !same(rows[j.id], pick(j)));
  const change = (id, patch) => setRows((r) => {
    const next = { ...r[id], ...patch };
    KEYS.forEach((k) => { if (!next[k]) next.visible = { ...next.visible, [k]: false }; });
    return { ...r, [id]: next };
  });
  const save = async () => {
    setBusy(true);
    try {
      const { data: res } = await http.put("/admin/careers/jobs-details", { items: dirty.map((j) => rows[j.id]) });
      toast.success(`Saved ${res?.saved ?? dirty.length} job${dirty.length === 1 ? "" : "s"}`);
      ["admin-jobs", "careers-jobs", "careers-job"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="admin-job-details">
      <AdminTitle title="Job Details · Quick Edit" sub="Set pay, location, employment type and work arrangement for every position. A detail only appears on the website when it is filled in and set to Shown publicly.">
        <Link to="/admin/careers" className="btn-ghost" data-testid="details-back-link"><ArrowLeft className="w-4 h-4" /> Job listings</Link>
        <button className="btn-ghost" disabled={!dirty.length || busy} onClick={() => setRows(Object.fromEntries(jobs.map((j) => [j.id, pick(j)])))} data-testid="details-reset-button"><RotateCcw className="w-4 h-4" /> Discard</button>
        <button className="btn-gold" disabled={!dirty.length || busy} onClick={save} data-testid="details-save-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes{dirty.length ? ` (${dirty.length})` : ""}</button>
      </AdminTitle>
      {isLoading ? <p className="text-slate-400">Loading…</p> : (
        <div className="overflow-x-auto border hairline">
          <table className="w-full text-sm min-w-[1100px]" data-testid="details-table">
            <thead className="bg-[var(--ink-2)] text-xs font-mono uppercase text-slate-400 text-left">
              <tr><th className="p-3">Position</th><th className="p-3">Employment type</th><th className="p-3">Work arrangement</th><th className="p-3">Location</th><th className="p-3">Compensation (optional)</th><th className="p-3">Job published</th></tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {jobs.map((j) => {
                const r = rows[j.id];
                if (!r) return null;
                const isDirty = !same(r, pick(j));
                return (
                  <tr key={j.id} className={isDirty ? "bg-[rgba(212,175,55,0.05)]" : ""} data-testid={`details-row-${j.slug}`}>
                    <td className="p-3 align-top"><p className="text-slate-100 font-medium">{j.title}</p><p className="text-xs text-slate-400">{j.department}</p>{isDirty && <p className="text-[0.7rem] text-gold mt-1">Unsaved changes</p>}</td>
                    <Cell row={r} k="employment_type" onChange={(p) => change(j.id, p)}>
                      <select className="field !py-2 !text-sm" value={r.employment_type} onChange={(e) => change(j.id, { employment_type: e.target.value })} data-testid={`details-type-${j.slug}`}><option value="">Not set</option>{EMP_TYPES.map((o) => <option key={o}>{o}</option>)}</select>
                    </Cell>
                    <Cell row={r} k="work_arrangement" onChange={(p) => change(j.id, p)}>
                      <select className="field !py-2 !text-sm" value={r.work_arrangement} onChange={(e) => change(j.id, { work_arrangement: e.target.value })} data-testid={`details-arrangement-${j.slug}`}><option value="">Not set</option>{ARRANGEMENTS.map((o) => <option key={o}>{o}</option>)}</select>
                    </Cell>
                    <Cell row={r} k="location" onChange={(p) => change(j.id, p)}>
                      <input className="field !py-2 !text-sm" placeholder="Not set" value={r.location} maxLength={160} onChange={(e) => change(j.id, { location: e.target.value })} data-testid={`details-location-${j.slug}`} />
                    </Cell>
                    <Cell row={r} k="compensation" onChange={(p) => change(j.id, p)}>
                      <input className="field !py-2 !text-sm" placeholder="Not set" value={r.compensation} maxLength={160} onChange={(e) => change(j.id, { compensation: e.target.value })} data-testid={`details-compensation-${j.slug}`} />
                    </Cell>
                    <td className="p-3 align-top">
                      <label className="flex items-center gap-2 text-xs text-slate-200"><input type="checkbox" className="accent-[var(--gold)] w-4 h-4" checked={r.published} onChange={(e) => change(j.id, { published: e.target.checked })} data-testid={`details-published-${j.slug}`} />{r.published ? "Published" : "Unpublished"}</label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
